import { ChangeDetectionStrategy, Component, computed, inject, viewChild, ViewEncapsulation } from "@angular/core";
import { LayoutWidget } from "../../layouts/se.layout.widget.abstract";
import { Layouts } from "../../layouts/se.layouts";
import { isOrgPageFormUseKey, ORG_PAGE_FORM_USE_KEYS, SeOrgDetailsStore } from "../se.org.details.store";
import { INGESTION_POINT_FORM_USES } from "../../../data-ingestion/data.ingestion.const";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../../s25-modal/s25.modal.component";
import { IngestionFormComponent } from "../../../data-ingestion/ingestion.form.component";
import { createOrgEditHydrate } from "../../../data-ingestion/org.form.hydrate.factory";

@Component({
    standalone: true,
    selector: "s25-ng-se-org-form-link-widget",
    template: `
        @if (resolvedForm(); as form) {
            <div class="form-link-container">
                <s25-ng-button [type]="'primary'" [onClick]="openForm">
                    {{ label() }}
                </s25-ng-button>
            </div>
            <s25-ng-modal #formModal [title]="form.ingestionPointName ?? label()" [size]="'lg'">
                <ng-template #s25ModalBody>
                    <s25-ng-ingestion-form
                        [ingestionPointId]="form.ingestionPointId!"
                        [runtimeHydrate]="runtimeHydrate()"
                        (onSubmit)="onFormSubmitted()"
                        (onClose)="formModal.close()"
                    />
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: `
        .form-link-container {
            display: flex;
            justify-content: flex-start;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25ButtonComponent, S25ModalModule, IngestionFormComponent],
})
export class SeOrgFormLinkWidgetComponent extends LayoutWidget<typeof SeOrgFormLinkWidgetComponent> {
    static readonly META = {
        name: "Form Link Button",
        info: "A button that opens an organization form. The form is resolved per organization from its use type.",
        settings: [
            {
                id: "useKey",
                label: "Form Type",
                type: "field" as const,
                fieldType: "single-select" as const,
                options: {
                    dropdownOptions: ORG_PAGE_FORM_USE_KEYS.map(
                        (useKey): DropDownItem<string> => ({
                            itemId: useKey,
                            itemName: INGESTION_POINT_FORM_USES[useKey],
                        }),
                    ),
                },
            },
            {
                id: "label",
                label: "Button Label",
                type: "field" as const,
                fieldType: "text" as const,
            },
        ],
        defaultSettings: {
            useKey: "orgJoin",
            label: "Join Organization",
        },
    } as const satisfies Layouts.WidgetStatic;

    protected readonly orgStore = inject(SeOrgDetailsStore);
    private readonly formModal = viewChild<S25ModalComponent>("formModal");

    protected readonly useKey = computed(() => {
        const useKey = this.settingValues()["useKey"];
        return isOrgPageFormUseKey(useKey) ? useKey : undefined;
    });
    protected readonly label = computed(
        () => this.settingValues()["label"] || SeOrgFormLinkWidgetComponent.META.defaultSettings.label,
    );

    /** The first form for the configured use: org-scoped when the org has one, otherwise global. */
    protected readonly resolvedForm = computed(() => {
        const useKey = this.useKey();
        if (!useKey || !this.orgStore.canUseForm(useKey)) return undefined;
        return this.orgStore.formsForUse(useKey)[0];
    });

    protected readonly runtimeHydrate = computed(() => {
        const orgId = this.orgStore.orgId();
        const useKey = this.useKey();
        if (!orgId || (useKey !== "orgEdit" && useKey !== "orgJoin")) return undefined;
        return createOrgEditHydrate(orgId);
    });

    @Bind
    async openForm() {
        await this.formModal()?.open();
    }

    async onFormSubmitted() {
        await this.formModal()?.close();
        await this.orgStore.reloadDetails();
    }
}
